import React, { Component } from 'react';
import { reloadPage } from '../../utils/browserLocation';

// The last thing standing when the store, the router's children or a code-split chunk are what
// broke (A-3), so this file imports react and NOTHING else but utils/browserLocation, a leaf with
// no imports of its own: no connect, no reactstrap, no stylesheet. Anything it pulled in would be
// one more thing that can fail while it is the only component left able to say so. For the same reason the fallback below is styled inline rather
// than by class — see STYLE.

// localStorage keys. `set-temp-UIVersion` is the pin actions/api.js:7 reads at import time; it is
// already spelled inline in four places in production code (actions/api.js:7, 11, 12 and
// components/ErrorModal/index.js:20), and a new file does not need to be the fifth.
const TRANSLATED_TEXT_KEY = 'translated-text';
const TEMP_VERSION_KEY = 'set-temp-UIVersion';

// Hard-coded English, compiled into the main bundle. Every other copy in the app arrives over the
// network or out of localStorage — neither is available on the path that gets us here.
const EN = {
  title: 'Something went wrong',
  message: 'This page did not finish loading. Reload to try again.',
  reload: 'Reload',
};

// STYLE. Inline, not classes: a fallback whose premise is "everything else may be broken" must not
// depend on any stylesheet having reached the bundle. `assets/style/app.scss` — where .Site-content
// lives (app.scss:2428) — is imported by src/index.js only in a commented-out line (index.js:12);
// it arrives through components/Header/index.js:45, and Header is itself a lazy chunk
// (containers/App/PrivateRoute.js:7), i.e. possibly the very chunk whose failure got us here. The
// bootstrap `btn` classes have the same problem. Plain readable text, sane spacing, a visible
// button; no design system. `app-error` survives only as a selector for the spec and for QA —
// nothing in the repo styles it.
const BOX_STYLE = {
  maxWidth: '32rem',
  margin: '4rem auto',
  padding: '0 1rem',
  textAlign: 'center',
  fontFamily: 'Roboto, Arial, Helvetica, sans-serif',
  lineHeight: 1.5,
  color: '#333333',
};
const TITLE_STYLE = { fontSize: '1.25rem', fontWeight: 'bold', margin: '0 0 0.75rem' };
const MESSAGE_STYLE = { fontSize: '1rem', margin: '0 0 1.5rem' };
const BUTTON_STYLE = {
  display: 'inline-block',
  padding: '0.5rem 1.5rem',
  fontSize: '1rem',
  lineHeight: 1.5,
  color: '#ffffff',
  backgroundColor: '#0a7c86',
  border: '1px solid #0a7c86',
  borderRadius: '4px',
  cursor: 'pointer',
};

// Guard the parse the same way containers/App/PrivateRoute.js:35-40 and utils/formValidate.js:351-356
// do: a malformed or empty `translated-text` makes JSON.parse throw, and throwing HERE is the one
// failure nothing above can catch — the boundary would take the page down with the bug it exists to
// report. The stored value is the whole user object (components/Header/index.js:103); the strings
// live in its flat `translator` dictionary.
const readTranslator = () => {
  let stored = null;
  try {
    stored = JSON.parse(localStorage.getItem(TRANSLATED_TEXT_KEY));
  } catch (e) {
    stored = null;
  }
  return (stored && stored.translator) || null;
};

// Truthiness is not enough: a leaf the API ships as an object or an array ({"translator":
// {"error_title": {}}}) is truthy, and handing it to JSX throws "Objects are not valid as a React
// child" INSIDE this render — again the one failure nothing above can catch. So the value has to be
// a string as well as present.
const copy = (translator, key, fallback) => {
  const value = translator && translator[key];
  return value && typeof value === 'string' ? value : fallback;
};

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
    this.reload = this.reload.bind(this);
  }

  // Suppresses the broken subtree on the SAME commit that threw. Without it React 16 unmounts the
  // whole tree and the visitor is left with a blank page (or, under <Suspense>, the spinner that
  // never goes away).
  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  // Reporting only — the render decision above must not depend on it. Two channels, and the second
  // one is not decoration:
  //
  // 1. console.error, for the browser console and for Sentry's breadcrumb trail.
  // 2. a re-throw on the next macrotask. With no boundary in the tree React 16 lets an uncaught
  //    render error escape to window.onerror, which is precisely where @sentry/browser's default
  //    GlobalHandlers integration listens; catching the error here would take it off that path, so
  //    adding this boundary would LOSE reporting that exists today. console.error is no
  //    substitute: in @sentry/browser 5 a console call is a breadcrumb, not an event — promoting it
  //    needs CaptureConsole from @sentry/integrations, which this project does not depend on.
  //    Throwing out of band keeps the fallback committed (React has already rendered it) while
  //    window.onerror still sees the error, and it costs this file no import.
  //
  // Sentry is NOT initialised here or by anything above: the app's only Sentry.init sits at module
  // scope of components/Login/index.js:54-58, a lazy chunk (containers/App/index.js:18) — so on a
  // failed-chunk boot, or for a returning authenticated user who never loads Login, there is no
  // Sentry at all and the re-throw is simply an uncaught error in the console, exactly as today.
  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] caught a render error:', error, info);
    setTimeout(() => {
      throw error;
    });
  }

  // A pinned temp UI version (actions/api.js:7) is one reason a chunk 404s, so it is dropped before
  // the page comes back. removeItem, not setItem(key, null): that writes the STRING "null", which
  // only works because api.js:45 sentinels it (`tempVer && tempVer !== 'null'`).
  // The write is guarded because the reload must happen either way — where localStorage throws
  // (quota, blocked cookies, Safari private mode) an unguarded call would leave the button inert
  // and the error box permanent, which is A-3 again; and React error boundaries do not catch throws
  // from event handlers, so nothing else would notice.
  // `reload()` takes no argument: `forceReload` was dropped from the spec and is ignored by current
  // engines. It would not have helped here anyway — this app serves navigations from the precached
  // /index.html (custom-sw.js:22), and no value of that argument has ever bypassed a service worker.
  reload() {
    try {
      localStorage.removeItem(TEMP_VERSION_KEY);
    } catch (e) {
      // Ignored on purpose: dropping the pin is best-effort, reloading is not.
    }
    // W3A-T10-1. A plain reload cannot recover a post-deploy chunk 404: custom-sw.js serves every
    // navigation from the precached /index.html
    // (registerNavigationRoute(getCacheKeyForURL("/index.html"))), and a reload IS a navigation —
    // so it gets the same stale document, pointing at chunk hashes the CDN no longer has, and
    // fails again. `skipWaiting()` + `clientsClaim()` mean a LATER load usually works, which is
    // why this looked intermittent rather than broken.
    //
    // So: drop the caches, unregister the worker, then reload — the reload then goes to the
    // network. index.js re-registers the worker on the next load, so offline support returns
    // immediately. Every step is best-effort and the reload happens either way: a Reload button
    // that silently does nothing is the very defect this component exists to prevent.
    const reloadNow = () => reloadPage();
    // Each step is attempted INDEPENDENTLY and each swallows its own failure. Chained, a cache
    // deletion that rejected skipped the unregistration entirely — and a `caches.keys()` that
    // threw SYNCHRONOUSLY (rather than rejecting) escaped before any handler was attached and
    // took the reload with it, leaving the button inert, which is the defect this component
    // exists to prevent.
    const attempt = (fn) => {
      try {
        return Promise.resolve(fn()).catch(() => null);
      } catch (e) {
        return Promise.resolve(null);
      }
    };
    const clearCaches = attempt(() => (
      (window.caches && window.caches.keys)
        ? window.caches.keys().then((names) => Promise.all(names.map((n) => window.caches.delete(n))))
        : null
    ));
    const dropWorker = attempt(() => (
      (navigator.serviceWorker && navigator.serviceWorker.getRegistration)
        ? navigator.serviceWorker.getRegistration().then((reg) => (reg ? reg.unregister() : null))
        : null
    ));
    Promise.all([clearCaches, dropWorker]).then(reloadNow, reloadNow);
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }
    const translator = readTranslator();
    return (
      <div className="app-error" style={BOX_STYLE}>
        <h5 style={TITLE_STYLE}>{copy(translator, 'error_title', EN.title)}</h5>
        <p style={MESSAGE_STYLE}>{copy(translator, 'error_msg', EN.message)}</p>
        <button type="button" style={BUTTON_STYLE} onClick={this.reload}>
          {copy(translator, 'reload_Btn', EN.reload)}
        </button>
      </div>
    );
  }
}

export default ErrorBoundary
